import { useState } from 'react';
import { Link } from 'react-router-dom';

import { PageMeta } from '@/components/PageMeta';
import { type FeatureDef, ROLE_CONFIG, type RoleId, SPOTLIGHTS, STATS } from '@/components/fonctionnalites/data';
import { ROUTES } from '@/config/routes';
import { ArrowRight, Building2, CheckCircle2, Globe, Search, Sparkles, User, Users } from 'lucide-react';

// ─── Page ─────────────────────────────────────────────────
export default function Fonctionnalites() {
  const [activeRole, setActiveRole] = useState<RoleId>('organizer');
  const cfg = ROLE_CONFIG[activeRole];

  return (
    <div className='bg-[#F8FAFC] text-slate-900 font-sans selection:bg-blue-100 min-h-screen'>
      <PageMeta
        title='Fonctionnalités'
        description='Découvrez toutes les fonctionnalités BoulesConnect : gestion des concours, inscriptions en ligne, paiements Stripe, résultats et podium — pour les organisateurs, clubs et joueurs.'
      />

      {/* ─── HERO ─────────────────────────────────────────── */}
      <section className='pt-40 pb-16 px-6'>
        <div className='max-w-5xl mx-auto text-center'>
          <div className='inline-flex items-center gap-2 bg-blue-50 border border-blue-100 px-4 py-2 rounded-full mb-8'>
            <Sparkles className='w-4 h-4 text-blue-600' />
            <span className='text-xs font-bold text-blue-700 uppercase tracking-wider'>
              3 rôles · 50+ fonctionnalités · 100% web
            </span>
          </div>

          <h1 className='text-5xl md:text-6xl font-extrabold text-slate-900 leading-[1.1] mb-6'>
            Une plateforme complète
            <br />
            <span className='bg-linear-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent'>
              pour chaque acteur
            </span>
          </h1>

          <p className='text-lg text-slate-500 max-w-2xl mx-auto mb-12 leading-relaxed'>
            BoulesConnect propose une expérience sur-mesure pour les{' '}
            <strong className='text-slate-700'>Organisateurs</strong>, <strong className='text-slate-700'>Clubs</strong>{' '}
            et <strong className='text-slate-700'>Joueurs</strong> — de l'inscription au podium final.
          </p>

          {/* Stats strip */}
          <div className='grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto'>
            {STATS.map(s => (
              <div
                key={s.label}
                className='bg-white rounded-2xl border border-slate-200 py-5 px-4 text-center shadow-sm'
              >
                <div className='text-3xl font-extrabold text-slate-900'>{s.value}</div>
                <div className='text-xs text-slate-500 mt-1 font-medium'>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SPOTLIGHTS ───────────────────────────────────── */}
      <section className='px-6 py-16 bg-slate-900'>
        <div className='max-w-6xl mx-auto'>
          <div className='text-center mb-12'>
            <p className='text-xs font-bold text-slate-400 uppercase tracking-widest mb-3'>Fonctionnalités phares</p>
            <h2 className='text-3xl font-extrabold text-white'>Ce qui nous différencie</h2>
          </div>
          <div className='grid md:grid-cols-3 gap-6'>
            {SPOTLIGHTS.map(s => (
              <div
                key={s.title}
                className={`relative overflow-hidden rounded-3xl p-8 shadow-xl ${s.glow} bg-slate-800 border border-slate-700 group hover:-translate-y-1 transition-all duration-300`}
              >
                <div
                  className={`absolute -top-12 -right-12 w-40 h-40 rounded-full bg-linear-to-br ${s.gradient} opacity-15 group-hover:opacity-25 transition-opacity duration-300`}
                />
                <div
                  className={`w-12 h-12 rounded-2xl bg-linear-to-br ${s.gradient} flex items-center justify-center mb-6 shadow-lg`}
                >
                  <s.icon className='w-6 h-6 text-white' />
                </div>
                <span className='text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 block'>{s.tag}</span>
                <h3 className='text-xl font-bold text-white mb-3'>{s.title}</h3>
                <p className='text-sm text-slate-400 leading-relaxed'>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── ROLE TABS ────────────────────────────────────── */}
      <div className='sticky top-16 z-30 bg-white/80 backdrop-blur-sm border-b border-slate-200 shadow-sm'>
        <div className='max-w-5xl mx-auto px-6 py-3 flex items-center gap-3'>
          <span className='text-xs font-bold text-slate-400 uppercase tracking-wider mr-2 hidden sm:block'>
            Profil :
          </span>
          {(Object.entries(ROLE_CONFIG) as [RoleId, (typeof ROLE_CONFIG)[RoleId]][]).map(([id, c]) => (
            <button
              key={id}
              onClick={() => setActiveRole(id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 cursor-pointer ${
                activeRole === id ? c.activeTab : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'
              }`}
            >
              <span>{c.emoji}</span>
              <span>{c.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ─── FEATURE GRID ─────────────────────────────────── */}
      <section className={`px-6 py-20 ${cfg.sectionBg} transition-colors duration-300`}>
        <div className='max-w-7xl mx-auto'>
          {/* Section header */}
          <div className='flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-6'>
            <div className='flex items-center gap-4'>
              <div className={`w-14 h-14 ${cfg.activeBg} rounded-2xl flex items-center justify-center shadow-lg`}>
                <cfg.icon className='w-7 h-7 text-white' />
              </div>
              <div>
                <p className={`text-xs font-bold uppercase tracking-widest ${cfg.color} mb-0.5`}>{cfg.label}</p>
                <h2 className='text-3xl font-extrabold text-slate-900'>
                  {activeRole === 'organizer' && 'Gérez vos concours'}
                  {activeRole === 'club' && 'Gérez votre club'}
                  {activeRole === 'player' && 'Vivez la compétition'}
                </h2>
              </div>
            </div>
            <Link
              to={cfg.ctaHref}
              className={`shrink-0 flex items-center gap-2 ${cfg.activeBg} text-white px-6 py-3 rounded-xl text-sm font-bold shadow-lg transition hover:opacity-90`}
            >
              {cfg.cta} <ArrowRight className='w-4 h-4' />
            </Link>
          </div>

          {/* Cards */}
          <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-6'>
            {cfg.features.map(feat => (
              <FeatureCard key={feat.title} feat={feat} accentBorder={cfg.accentBorder} accentColor={cfg.color} />
            ))}
          </div>
        </div>
      </section>

      {/* ─── COMPARISON STRIP ─────────────────────────────── */}
      <section className='px-6 py-16 bg-slate-50 border-y border-slate-200'>
        <div className='max-w-5xl mx-auto'>
          <h2 className='text-2xl font-extrabold text-slate-900 text-center mb-10'>Disponible sur tous les profils</h2>
          <div className='grid md:grid-cols-3 gap-px bg-slate-200 rounded-3xl overflow-hidden shadow-sm'>
            {(
              [
                {
                  role: 'Organisateur',
                  bg: 'bg-blue-600',
                  icon: Building2,
                  features: [
                    'Création de concours',
                    'Gestion des équipes',
                    'Paiements Stripe Connect Standard',
                    'Saisie des résultats',
                    'Workflow automatisé',
                    'Export CSV complet',
                  ],
                },
                {
                  role: 'Club',
                  bg: 'bg-emerald-600',
                  icon: Users,
                  features: [
                    'Fiche club publique géolocalisée',
                    "Demandes d'adhésion en ligne",
                    'Annuaire des membres',
                    'Gestion des équipes',
                    'Historique compétitions',
                    'Export CSV',
                  ],
                },
                {
                  role: 'Joueur',
                  bg: 'bg-orange-500',
                  icon: User,
                  features: [
                    'Recherche géolocalisée PostGIS',
                    'Inscription & paiement CB',
                    'Podium & résultats publics',
                    'Espace personnel',
                    'Profil FFPJP & stats',
                    'OAuth Google',
                  ],
                },
              ] as const
            ).map(col => (
              <div key={col.role} className='bg-white p-8'>
                <div className={`w-10 h-10 ${col.bg} rounded-xl flex items-center justify-center mb-4`}>
                  <col.icon className='w-5 h-5 text-white' />
                </div>
                <h3 className='font-extrabold text-slate-900 text-lg mb-5'>{col.role}</h3>
                <ul className='space-y-3'>
                  {col.features.map(f => (
                    <li key={f} className='flex items-start gap-2.5 text-sm text-slate-600'>
                      <CheckCircle2 className='w-4 h-4 text-slate-400 shrink-0 mt-0.5' />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA FINAL ────────────────────────────────────── */}
      <section className='px-6 py-28 bg-linear-to-br from-blue-600 via-blue-700 to-indigo-800 text-white relative overflow-hidden'>
        <div className='absolute inset-0 opacity-10 pointer-events-none'>
          <div className='absolute top-8 right-24 w-72 h-72 rounded-full bg-white/20' />
          <div className='absolute bottom-8 left-12 w-48 h-48 rounded-full bg-white/10' />
        </div>
        <div className='max-w-4xl mx-auto text-center relative z-10'>
          <div className='inline-flex items-center gap-2 bg-white/10 border border-white/20 px-4 py-2 rounded-full mb-8 text-xs font-bold uppercase tracking-wider'>
            <Globe className='w-4 h-4' />
            Commission Partenaire 2 % + 0,30 € (min 0,75 €) · 0 € fixe · Concours illimités · Sans engagement
          </div>
          <h2 className='text-4xl md:text-5xl font-extrabold mb-6 leading-tight'>
            Prêt à digitaliser
            <br />
            vos concours ?
          </h2>
          <p className='text-blue-100 text-lg mb-10 max-w-xl mx-auto'>
            Rejoignez les organisateurs, clubs et joueurs qui font confiance à BoulesConnect pour gérer leurs
            compétitions.
          </p>
          <div className='flex flex-col sm:flex-row items-center justify-center gap-4'>
            <Link
              to={ROUTES.REGISTER}
              className='w-full sm:w-auto bg-white text-blue-700 px-10 py-4 rounded-2xl font-bold text-lg shadow-xl hover:bg-blue-50 transition flex items-center justify-center gap-2'
            >
              Démarrer gratuitement <ArrowRight className='w-5 h-5' />
            </Link>
            <Link
              to={ROUTES.SEARCH_CONCOURS}
              className='w-full sm:w-auto border-2 border-white/30 text-white px-10 py-4 rounded-2xl font-bold text-lg hover:bg-white/10 transition flex items-center justify-center gap-2'
            >
              <Search className='w-5 h-5' /> Explorer les concours
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

// ─── FeatureCard ──────────────────────────────────────────
function FeatureCard({
  feat,
  accentBorder,
  accentColor,
}: {
  feat: FeatureDef;
  accentBorder: string;
  accentColor: string;
}) {
  return (
    <div
      className={`bg-white rounded-3xl p-7 border ${accentBorder} hover:-translate-y-1 hover:shadow-xl transition-all duration-300 group`}
    >
      <div className='flex items-start justify-between mb-5'>
        <div
          className={`w-11 h-11 bg-slate-100 rounded-xl flex items-center justify-center group-hover:bg-slate-200 transition-colors`}
        >
          <feat.icon className={`w-5 h-5 ${accentColor}`} />
        </div>
        {feat.badge && (
          <span
            className={`text-xs font-bold px-2.5 py-1 rounded-full ${feat.badgeColor ?? 'bg-slate-100 text-slate-600'}`}
          >
            {feat.badge}
          </span>
        )}
      </div>
      <h3 className='text-base font-bold text-slate-900 mb-4'>{feat.title}</h3>
      <ul className='space-y-2.5'>
        {feat.items.map(item => (
          <li key={item} className='flex items-start gap-2.5'>
            <CheckCircle2 className='w-4 h-4 text-slate-300 shrink-0 mt-0.5' />
            <span className='text-sm text-slate-600 leading-snug'>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
